import { CheckCircleOutline, Computer, Style } from '@mui/icons-material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import TranslateIcon from '@mui/icons-material/Translate';
import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import { blue, green, grey, orange, red } from '@mui/material/colors';

import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { anbieter, klassifizierung, sapStatus, type Veranstaltung } from '../../../dtos';

const getAbsageGrund = (veranstaltung: Veranstaltung): string =>
  veranstaltung.absagegrund ? `- ${veranstaltung.absagegrund}` : '';

const getAbsageDatum = (veranstaltung: Veranstaltung) =>
  veranstaltung.absagedatum ? ` (${veranstaltung.absagedatum.toLocaleDateString()})` : '';

const redColor = red[500];
const greenColor = green[500];
const blueColor = blue[500];
const greyColor = grey[500];
const orangeColor = orange[500];
const anbieterColor = '#1ed760';

export const VeranstaltungHeaderCard = ({ veranstaltung }: { readonly veranstaltung: Veranstaltung }) => (
  <Card
    sx={{
      marginTop: 2,
      marginBottom: 2,
      ...(veranstaltung.sapStatus === sapStatus.ABGESAGT
        ? {
            backgroundColor: 'red',
            color: 'white',
          }
        : {}),
    }}
  >
    <CardContent>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignContent="space-between">
        <Stack spacing={1}>
          <Typography variant="headingMd">
            {veranstaltung.sapStatus === sapStatus.ABGESAGT && <b>ABGESAGT! </b>}
            {veranstaltung.title}
            {veranstaltung.absagegrund || veranstaltung.absagedatum
              ? `${getAbsageGrund(veranstaltung)}${getAbsageDatum(veranstaltung)}`
              : ''}
          </Typography>
          <small>Format {veranstaltung.format}</small>
          <Typography variant="headingXxs">
            {veranstaltung.ortBezeichnung ?? 'VERANSTALTUNG-ORT'} |{' '}
            {veranstaltung.experteBlockungen[0]?.experte
              ? `${veranstaltung.experteBlockungen[0].experte.vorname} ${veranstaltung.experteBlockungen[0].experte.nachname}`
              : '-'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label={veranstaltung.sapStatus?.toUpperCase() ?? 'kein SapStatus'}
              icon={<FlightTakeoffIcon htmlColor="red" color="error" />}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: greyColor,
                svg: { color: 'white !important' },
              }}
            />
            <Chip
              label={veranstaltung.geschaeftsbereich}
              icon={<ApartmentIcon />}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: blueColor,
                svg: { color: 'white !important' },
              }}
            />
            {veranstaltung.virtualclassroom && (
              <Chip
                label="Online Seminar"
                icon={<CheckCircleOutline />}
                size="small"
                sx={{
                  color: 'white',
                  backgroundColor: greenColor,
                  svg: { color: 'white !important' },
                }}
              />
            )}
            {veranstaltung.language && (
              <Chip
                label={veranstaltung.language}
                icon={<TranslateIcon />}
                size="small"
                sx={{
                  color: 'white',
                  backgroundColor: orangeColor,
                  svg: { color: 'white !important' },
                }}
              />
            )}
            {veranstaltung.anbieter && veranstaltung.anbieter !== 'AKA' && (
              <Chip
                label={anbieter[veranstaltung.anbieter]}
                icon={<Computer />}
                size="small"
                sx={{
                  color: 'white',
                  backgroundColor: anbieterColor,
                  svg: { color: 'white !important' },
                }}
              />
            )}
            <Chip
              label={veranstaltung.klassifizierung}
              icon={<Style />}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: redColor,
                svg: { color: 'white !important' },
              }}
            />
          </Stack>
        </Stack>
        <Grid container width="45ch" columnSpacing={1}>
          <Grid size={6}>
            <b>AKA-Veranstaltung-ID:</b>
          </Grid>
          <Grid size={6}>
            <b>{veranstaltung.akaVeranstaltungId}</b>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            <CopyToClipboard value={String(veranstaltung.akaVeranstaltungId)} />
          </Grid>
          <Grid size={6}>SAP-Veranstaltung-ID (E):</Grid>
          <Grid size={6}>
            <b>{veranstaltung.sapid}</b>
            <CopyToClipboard value={String(veranstaltung.sapid)} />
          </Grid>
          <Grid size={6}>Seminarrunden-ID:</Grid>
          <Grid size={6}>
            <b>
              {veranstaltung.seminarrundenid}
              {veranstaltung.semiarrundenkuerzel && `/${veranstaltung.semiarrundenkuerzel}`}
            </b>
          </Grid>
          <Grid size={6}>SAP-Veranstaltungstyp-ID (D):</Grid>
          <Grid size={6}>
            <b>{veranstaltung.produktid}</b>
          </Grid>
          <Grid size={6}>S4 Material-Produkt-ID:</Grid>
          <Grid size={6}>
            <b>{veranstaltung.s4MaterialnummerProdukt}</b>
          </Grid>
          <Grid size={6}>ECC Medienausgabe:</Grid>
          <Grid size={6}>
            <b>{veranstaltung.eccMaterialnummerMedienausgabe}</b>
          </Grid>
          <Grid size={6}>S4 Verkaufs-/Leistungseinheit:</Grid>
          <Grid size={6}>
            <b>{veranstaltung.s4MaterialnummerVerkaufseinheit}</b>
          </Grid>
          <Grid size={6}>Debitor-ID:</Grid>
          <Grid size={6}>
            <b>{veranstaltung.debitorId}</b>
          </Grid>
          <Grid size={6}>Anbieter-Event-ID:</Grid>
          <Grid size={6}>
            <b>{veranstaltung.anbieterEventId}</b>
          </Grid>
          <Grid size={6}>AKA-Auftrag-ID:</Grid>
          <Grid size={6}>
            <b>{veranstaltung.akaAuftragId}</b>
          </Grid>
          {(veranstaltung.klassifizierung === klassifizierung.DURCHGANG ||
            veranstaltung.klassifizierung === klassifizierung.MODULTERMIN) && (
            <>
              <Grid size={6}>AKA-Durchgang-ID:</Grid>
              <Grid size={6}>
                <b>{veranstaltung.akaDurchgangId}</b>
              </Grid>
            </>
          )}
          <Grid size={6}>Letzte Änderung:</Grid>
          <Grid size={6}>
            <b>{new Date(veranstaltung.lastmodified).toLocaleString()}</b>
          </Grid>
        </Grid>
      </Box>
    </CardContent>
  </Card>
);
